/**
 * Authenticated fetch wrapper.
 * Automatically adds Authorization header with token from localStorage.
 */

const AUTH_STORAGE_KEY = 'admission-system-auth';

function getAuthToken(): string {
  try {
    const authData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!authData) return '';
    const parsed = JSON.parse(authData);
    return parsed.accessToken || '';
  } catch {
    return '';
  }
}

/**
 * Authenticated fetch wrapper.
 * Automatically adds Authorization header.
 * Handles 401 Unauthorized by clearing auth and redirecting to login.
 *
 * @param url - The URL to fetch
 * @param options - Fetch options
 * @returns The fetch response
 */
export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getAuthToken();
  const headers = new Headers(options.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized - clear auth and redirect to login
  if (response.status === 401) {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    // Redirect to login page
    globalThis.location.href = '/login';
    // Return a mock response to prevent further processing
    return new Response(JSON.stringify({ error: 'Unauthorized' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  return response;
}

/**
 * Authenticated GET request.
 */
export async function authGet(url: string): Promise<Response> {
  return authFetch(url, { method: 'GET' });
}

/**
 * Authenticated POST request with JSON body.
 */
export async function authPost(url: string, data: unknown): Promise<Response> {
  return authFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

/**
 * Authenticated PUT request with JSON body.
 */
export async function authPut(url: string, data: unknown): Promise<Response> {
  return authFetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

/**
 * Authenticated DELETE request.
 */
export async function authDelete(url: string): Promise<Response> {
  return authFetch(url, { method: 'DELETE' });
}
